import { useCallback, useEffect, useRef, useState } from 'react';
import type { RealItem } from '../data/types';
import { headlineScaleStyle } from './headlineScale';

/* Shown after REVEAL on a REAL headline: the REAL stamp slams over the headline, holds ~3s,
   then the live article opens IN A NEW TAB and the game stays exactly where it was.

   ── Why a new tab (changed 2026-08-10) ──
   The prototype did window.location.replace(url), which left the site entirely. Back returned
   to the game, but closing the article tab — the natural thing to do — closed the round, and
   restarting mid-episode is a real cost on camera. The game now never unmounts: he reads the
   article in the new tab, closes it, and the round is still on screen with the verdict marked.

   ── Why the stamp is over OUR headline, not the news site ──
   Unchanged, and still a hard browser limit: a page cannot draw on top of cnbc.com. Browsers
   forbid one origin from scripting another, and news sites send X-Frame-Options /
   frame-ancestors that block being iframed at all. The client accepted this. Do not attempt
   an iframe overlay; it fails silently on most outlets.

   ── Popup blocking ──
   window.open() outside a user gesture is blocked. Chrome and Firefox keep "transient user
   activation" alive for about five seconds after a click, and the hold is 3.2s, so the open
   normally succeeds on the strength of the REVEAL click. It is not guaranteed — some browsers
   and hardened profiles are stricter — so a blocked open is DETECTED and turned into a large
   button he can click instead. Silently failing to open the article mid-take is the one
   outcome worth writing extra code to avoid. */

const HOLD_MS = 3200;
const FADE_AT_MS = 2600;

interface Props {
  item: RealItem;
  headlineSize?: string;
  stampUrl?: string | null;
  /** Test seam. Must return the opened window, or null when the browser blocked it. */
  openArticle?: (url: string) => Window | null;
  /** Called once the article is open, so the game can come back to the headline. */
  onOpened: () => void;
  onCancel: () => void;
}

function defaultOpen(url: string): Window | null {
  // Not `window.open(url, '_blank', 'noopener')`: that form always returns null, so a blocked
  // popup would be indistinguishable from a successful one. Open, then sever opener by hand —
  // same protection, and we can still tell whether it worked.
  const win = window.open(url, '_blank');
  if (win) win.opener = null;
  return win;
}

export default function RealReveal({ item, headlineSize, stampUrl, openArticle, onOpened, onCancel }: Props) {
  const [fading, setFading] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const cancelled = useRef(false);

  const open = useCallback(() => {
    const win = (openArticle ?? defaultOpen)(item.sourceUrl);
    if (win) {
      setBlocked(false);
      onOpened();
      return true;
    }
    setBlocked(true);
    return false;
  }, [item.sourceUrl, onOpened, openArticle]);

  useEffect(() => {
    cancelled.current = false;
    const fade = window.setTimeout(() => {
      if (!cancelled.current) setFading(true);
    }, FADE_AT_MS);
    const go = window.setTimeout(() => {
      if (!cancelled.current) open();
    }, HOLD_MS);

    return () => {
      // If this unmounts — Esc, Back, a bfcache restore — the pending open must die with it,
      // or a tab appears unprompted, on camera.
      cancelled.current = true;
      window.clearTimeout(fade);
      window.clearTimeout(go);
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelled.current = true;
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="real-reveal" style={headlineScaleStyle(headlineSize, item.headline)} data-testid="real-reveal">
      <div className="outlet">{item.sourceName ? `Source · ${item.sourceName}` : ''}</div>
      <div className="headline">{item.headline}</div>

      {blocked ? (
        // The browser refused the tab. Say so plainly and give him one thing to click.
        <div className="blocked-open" data-testid="popup-blocked">
          <button className="reveal-btn" onClick={() => open()}>OPEN THE ARTICLE</button>
          <div className="loading">Your browser blocked the new tab — allow pop-ups for this site to skip this step</div>
        </div>
      ) : (
        <div className="loading">{fading ? 'Opening the article in a new tab…' : ' '}</div>
      )}

      <div className={`stamp-overlay${fading ? ' fading' : ''}`}>
        {stampUrl ? (
          <img className="stamp-img" src={stampUrl} alt="REAL" />
        ) : (
          <div className="stamp real">REAL</div>
        )}
      </div>
      <div className="escape-hint">Esc = back to the game without opening the article</div>
    </div>
  );
}
